import { ReactNode, useState, useEffect, useRef } from "react";
import {
  HOME_DECOR_ZONES,
  type HomeDecorZone,
} from "../../data/decor/bubbles";
import { BubbleField } from "./BubbleField";

type DecorZoneProps = {
  zone: HomeDecorZone;
  children: ReactNode;
  bubblesVisible?: boolean;
};

/**
 * Shared bubble + veil layer for consecutive white/transparent sections.
 * Delimited sections (gray, tinted panels) sit outside this wrapper.
 */
export function DecorZone({
  zone,
  children,
  bubblesVisible,
}: DecorZoneProps) {
  const { bubbles, veil } = HOME_DECOR_ZONES[zone];
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // If bubblesVisible is explicitly managed by the parent, skip local intersection check
    if (bubblesVisible !== undefined) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect(); // Keep it visible once entered
        }
      },
      { rootMargin: "600px" } // Fade in slightly before the zone enters the viewport
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [bubblesVisible]);

  // If bubblesVisible is explicitly passed from parent (like in heroAbout), respect it.
  // Otherwise, use our local intersection state.
  const activeVisible = bubblesVisible !== undefined ? bubblesVisible : inView;

  return (
    <div ref={containerRef} className="relative overflow-x-hidden">
      <BubbleField
        bubbles={bubbles}
        isStatic={zone !== "heroAbout"}
        className={[
          "absolute inset-0 z-0 bubble-field-transition",
          activeVisible ? "opacity-100" : "opacity-0",
        ].join(" ")}
      />
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{ background: veil }}
        aria-hidden="true"
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}
